import {
  createContext,
  MutableRefObject,
  ReactNode,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { createPortal } from "react-dom";
import { CalendarSchedule } from "./types";

interface DragContextValue {
  draggedEventRef: MutableRefObject<CalendarSchedule | null>;
  wasDraggingRef: MutableRefObject<boolean>;
  grabOffsetYRef: MutableRefObject<number>;
  pillRef: MutableRefObject<HTMLDivElement | null>;
  lineRef: MutableRefObject<HTMLDivElement | null>;
  // The pill/line's positioned ancestor, #weekly-calendar-body: the grid body
  // under the sticky day headers, which scrolls with the classes. DayDropLayer
  // places both from layout offsets inside it (a column's offsetLeft, minutes
  // × px), so no viewport coordinate is ever written as a style. Keeping them
  // inside the calendar avoids the Safari-only drift an earlier
  // position:fixed version had.
  containerEl: HTMLElement | null;
  // Clears the dragged-event ref and hides the pill + snap line. Shared by
  // drop (DayDropLayer) and dragend (useDraggableEvent) so both reset
  // identically.
  resetDragVisuals: () => void;
}

const DragContext = createContext<DragContextValue | null>(null);

export const DragProvider = ({ children }: { children: ReactNode }) => {
  const draggedEventRef = useRef<CalendarSchedule | null>(null);
  const wasDraggingRef = useRef(false);
  const grabOffsetYRef = useRef(0);
  const pillRef = useRef<HTMLDivElement | null>(null);
  const lineRef = useRef<HTMLDivElement | null>(null);
  // Looked up by id rather than threaded down as a ref: #weekly-calendar-body
  // (CalendarGrid.tsx) is a child of this provider, so it doesn't exist yet
  // during THIS component's own render; it's guaranteed to exist once mounted.
  const [containerEl, setContainerEl] = useState<HTMLElement | null>(null);

  useEffect(() => {
    setContainerEl(document.getElementById("weekly-calendar-body"));
  }, []);

  const resetDragVisuals = useCallback(() => {
    draggedEventRef.current = null;
    document.body.classList.remove("schedule-drag-active");
    if (pillRef.current) {
      pillRef.current.style.opacity = "0";
      pillRef.current.classList.remove("schedule-time-pill--invalid");
    }
    if (lineRef.current) {
      lineRef.current.style.opacity = "0";
      lineRef.current.classList.remove("schedule-snap-line--invalid");
    }
  }, []);

  const value = useMemo<DragContextValue>(
    () => ({
      draggedEventRef,
      wasDraggingRef,
      grabOffsetYRef,
      pillRef,
      lineRef,
      containerEl,
      resetDragVisuals,
    }),
    [containerEl, resetDragVisuals],
  );

  return (
    <DragContext.Provider value={value}>
      {children}
      {containerEl &&
        createPortal(
          <>
            {/* No manual font-family/font-size compensation here (unlike the old
                portal-to-<html> version) — this now lives inside <body>'s normal
                cascade, at the same scale as the rest of the calendar, so
                it inherits the app's real font correctly instead of approximating
                it by hand. */}
            <div ref={pillRef} className="schedule-time-pill" aria-hidden />
            <div ref={lineRef} className="schedule-snap-line" aria-hidden />
          </>,
          containerEl,
        )}
    </DragContext.Provider>
  );
};

// When not dragging, set the context values as null
const noopDragContext: DragContextValue = {
  draggedEventRef: { current: null },
  wasDraggingRef: { current: false },
  grabOffsetYRef: { current: 0 },
  pillRef: { current: null },
  lineRef: { current: null },
  containerEl: null,
  resetDragVisuals: () => {},
};

export const useDragContext = (): DragContextValue => {
  return useContext(DragContext) ?? noopDragContext;
};
